import { Text } from "@primer/react";
import { PageHeading } from "../components/PageHeading/PageHeading.jsx";
import { SampleProfileAction } from "../components/SampleProfileAction/SampleProfileAction.jsx";
import { Section } from "../components/Section/Section.jsx";
import { shortcutGroups } from "../lib/shortcuts/shortcutRegistry.js";
import { useApplicationDocument } from "../storage/useApplicationDocument.js";
import packageMetadata from "../../../package.json";
import "./HelpPage.css";

const setupSteps = [
  { href: "#/properties", title: "Define properties", description: "Describe the classifications that matter to your portfolio." },
  { href: "#/assets", title: "Add assets", description: "Add holdings and assign the appropriate property values." },
  { href: "#/allocation", title: "Record allocation", description: "Enter the current percentage held in each asset." },
  { href: "#/policies", title: "Add policies", description: "Set required limits and preferred ranges for exposures and concentration." }
];

export function HelpPage() {
  const application = useApplicationDocument();
  return <article className="help-page narrow-page">
    <PageHeading actions={<Text className="help-version">v{packageMetadata.version}</Text>} description="Learn the core workflow and explore a working portfolio.">Help</PageHeading>
    <Section heading="What Regulator is" description="A flexible framework for understanding your portfolio.">
      <ul className="help-highlights">
        <li><strong>Multiple perspectives</strong><Text>Use Browser views to examine the same allocation through different classifications and groupings.</Text></li>
        <li><strong>Your own policies</strong><Text>Define required limits and preferred ranges, then track violations, advisories, and compliance.</Text></li>
        <li><strong>Portfolio-neutral</strong><Text>Use it with passive or active portfolios, in any country or currency. Regulator works with allocation percentages.</Text></li>
        <li><strong>Flexible by design</strong><Text>Create the properties and policies that matter to you instead of adopting a hardcoded portfolio template.</Text></li>
      </ul>
    </Section>
    <Section heading="How Regulator works" description="Configuration flows into one compliance result.">
      <ol className="help-steps">{setupSteps.map((step) => <li key={step.href}>
        <a href={step.href}>{step.title}</a>
        <Text>{step.description}</Text>
      </li>)}</ol>
    </Section>
    <Section heading="Explore an example" description="The sample is isolated from your own profiles.">
      <div className="help-sample">
        <Text>Creates a small, editable portfolio with properties, seven assets, a complete allocation, policies, and saved Browser views.</Text>
        <SampleProfileAction application={application} />
      </div>
    </Section>
    <Section heading="Keyboard shortcuts" description="Navigate common application actions without leaving the keyboard.">
      <div className="shortcut-groups">{shortcutGroups.map((group) => <section key={group.key}>
        <h3>{group.label}</h3>
        <dl>{group.shortcuts.map((shortcut) => <div key={shortcut.id}>
          <dt>{shortcut.keys.map((key) => <kbd key={key}>{key}</kbd>)}</dt>
          <dd>{shortcut.label}</dd>
        </div>)}</dl>
      </section>)}</div>
    </Section>
    <Section heading="Inspired by regulation" description="The discipline, adapted for personal portfolios.">
      <Text className="help-explanation">Regulator takes inspiration from frameworks such as UCITS, where diversification and concentration are expressed as explicit limits. It does not implement or certify legal or regulatory compliance; the properties and policies are yours.</Text>
    </Section>
    <Section heading="Your data" description="Regulator runs entirely in this browser.">
      <div className="help-data">
        <Text>No account or login is required. Use profile exports to share or copy one portfolio, and optional backups to protect and restore the complete application.</Text>
        <a href="#/profiles">Manage profiles and exports</a>
        <a href="#/settings">Configure backups</a>
      </div>
    </Section>
  </article>;
}
